import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  AppHeader,
  SearchField,
  StatusDot,
  Icon,
  WorldSwitcher,
  type HottrProduct,
} from '@/kit';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/cn';

const PRODUCT: HottrProduct = 'app';

const utilityClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex h-11 w-11 items-center justify-center rounded-sm',
    'transition-colors duration-base ease-mechanical',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
    isActive ? 'text-hottr-pink' : 'text-white/55 hover:text-white',
  );

/**
 * The one kit header — identity + utilities, no destinations.
 *
 * Screen System §1: the Orb owns navigation. Passing `items={[]}` keeps the
 * centre slot empty so Feed / Grid / Messages / World cannot live here.
 * `mobile` is on so this is also the 56px phone bar; PageShell must not draw
 * a second header underneath it.
 */
export const DesktopHeader = ({
  liveCount = 0,
  extraUtility,
}: {
  liveCount?: number;
  extraUtility?: ReactNode;
}) => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [worldsOpen, setWorldsOpen] = useState(false);

  const onSearch = (q: string) => {
    setSearchOpen(false);
    navigate(q ? `${ROUTES.grid}?q=${encodeURIComponent(q)}` : ROUTES.grid);
  };

  return (
    <>
      <AppHeader
        surface="app"
        homeTo={ROUTES.feed}
        items={[]}
        mobile
        layout="nav-center"
        className={worldsOpen ? 'z-50' : undefined}
        switcher={
          <WorldSwitcher
            current={PRODUCT}
            className="h-11"
            open={worldsOpen}
            onOpenChange={setWorldsOpen}
            onSelect={(product) => {
              if (product === PRODUCT) navigate(ROUTES.feed);
            }}
          />
        }
        lead={
          <>
            <span aria-hidden className="hidden h-5 w-px bg-white/10 md:block" />
            <div className="hidden items-center gap-2 md:flex">
              <StatusDot status="live" size={6} />
              <span className="font-display text-[10px] font-bold uppercase tracking-hottr-label text-white/72">
                Tonight · {String(liveCount).padStart(2, '0')} live
              </span>
            </div>
          </>
        }
        utility={
          <>
            {extraUtility ? (
              <div className="flex items-center gap-3 md:hidden">{extraUtility}</div>
            ) : null}
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-expanded={searchOpen}
              aria-label={searchOpen ? 'Close search' : 'Search'}
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-sm',
                'transition-colors duration-base ease-mechanical',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
                searchOpen ? 'text-hottr-pink' : 'text-white/55 hover:text-white',
              )}
            >
              <Icon name="search" size={20} />
            </button>
            <NavLink to={ROUTES.settings} className={utilityClass} aria-label="Settings">
              <Icon name="tune" size={20} />
            </NavLink>
            <NavLink
              to={ROUTES.profile('kasper')}
              className={utilityClass}
              aria-label="My profile"
            >
              <Icon name="account_circle" size={22} />
            </NavLink>
          </>
        }
      />

      {searchOpen && (
        <div className="sticky top-16 z-30 border-b border-white/[0.06] bg-hottr-ink/90 px-5 py-3 backdrop-blur-xl md:px-6 lg:px-10">
          <div className="mx-auto max-w-[520px]">
            <SearchField
              autoFocus
              placeholder="Search people, places and interests"
              label="Search people"
              onSubmit={onSearch}
            />
          </div>
        </div>
      )}
    </>
  );
};
